import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Newspaper, BookOpen, ExternalLink, ArrowRight, Youtube, Play, Radio } from 'lucide-react';

/**
 * Press, Blog & Social — homepage section that bundles three credibility
 * surfaces in one place: published press releases, the brand blog, and a
 * pointer to social channels. Drop into Home.js wherever the page benefits
 * from a "we're in the public conversation" beat.
 */

const PRESS_RELEASES = [
  {
    title: 'Cochrane\'s Latest Review Signals the End of an Era in Alzheimer\'s Research',
    summary:
      'IIY Phytoventra reflects on Cochrane\'s landmark systematic review and what the shifting evidence base means for the future of cognitive-health research and natural compound investigation.',
    href: 'https://pressat.co.uk/releases/cochranes-latest-review-signals-the-end-of-an-era-in-alzheimers-research-8c86a6fa7cb41034ff22bd65b3caf08c/',
    publisher: 'Pressat',
    date: '2026',
    tag: 'Science Commentary',
  },
  {
    title: 'BCM-95® Curcumin Draws Continued Interest in the Joint Health Category',
    summary:
      'Coverage of the sustained scientific and consumer attention around the patented BCM-95® curcumin complex — the same standardised extract powering BCM-95®.',
    href: 'https://pressat.co.uk/releases/bcm-95-curcumin-draws-continued-interest-in-the-joint-health-category-e02014743f6cea8fe43b0c2c3ac05b9f/',
    publisher: 'Pressat',
    date: '2026',
    tag: 'Joint Health',
  },
  {
    title: 'Understanding Curcumin Formulations: What to Look For in a Food Supplement',
    summary:
      'A consumer guide to evaluating curcumin supplements — bioavailability, extraction standards, clean-label certifications, and what genuinely separates clinically-studied formulations from commodity turmeric.',
    href: 'https://pressat.co.uk/releases/understanding-curcumin-formulations-what-to-look-for-in-a-food-supplement-e75abdadaed744c4f18c5a3462098fb6/',
    publisher: 'Pressat',
    date: '2026',
    tag: 'Consumer Guide',
  },
];

const fadeIn = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, delay },
});

export default function PressBlogSocialSection() {
  return (
    <section
      id="press-blog"
      className="relative py-20 md:py-28 bg-gradient-to-b from-white via-emerald-50/30 to-white"
      data-testid="press-blog-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        {/* Section header */}
        <motion.div {...fadeIn()} className="text-center mb-12 md:mb-16 max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] uppercase tracking-[0.28em] font-semibold mb-4 bg-emerald-100 text-emerald-900 border border-emerald-200">
            <Newspaper className="w-3 h-3" /> News &amp; Press
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-emerald-900 leading-tight">
            In the <em className="not-italic text-emerald-700">Public Conversation</em>
          </h2>
          <p className="mt-3 text-base md:text-lg text-muted-foreground">
            Published press releases, brand journal articles, and behind-the-scenes thinking from the IIY Phytoventra team.
          </p>
        </motion.div>

        {/* Featured video — Lyca Radio interview */}
        <motion.div {...fadeIn(0.05)} className="mb-12">
          <Link
            to="/lyca-radio-interview"
            data-testid="featured-lyca-interview"
            className="group grid md:grid-cols-2 gap-0 rounded-3xl overflow-hidden border border-emerald-200 bg-emerald-950 shadow-xl hover:shadow-2xl transition-all"
          >
            <div className="relative aspect-video md:aspect-auto md:min-h-[280px] overflow-hidden">
              <img
                src="/lyca-interview-poster.jpg"
                alt="IIY Phytoventra on Lyca Radio — Talking Points with Keith Vaz"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                loading="lazy"
              />
              <span className="absolute inset-0 bg-gradient-to-t from-emerald-950/60 to-transparent" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-400 text-emerald-950 shadow-xl group-hover:scale-105 transition-transform">
                  <Play className="w-7 h-7 fill-emerald-950 ml-1" />
                </span>
              </span>
            </div>
            <div className="p-7 md:p-9 flex flex-col justify-center">
              <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-amber-300 mb-3">
                <Radio className="w-3.5 h-3.5" /> Featured · As heard on Lyca Radio
              </span>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-white leading-tight mb-2">
                Our Co-Founder on “Talking Points with Keith Vaz”
              </h3>
              <p className="text-emerald-100/85 text-sm leading-relaxed mb-4">
                Jaz Sidhu joins broadcaster Keith Vaz on Lyca Radio 1458 to talk turmeric heritage,
                high-absorption BCM-95® curcumin and the Invest In Yourself philosophy.
              </p>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-300 group-hover:gap-2.5 transition-all">
                Watch the interview <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
        </motion.div>

        {/* Top row: Press releases */}
        <div className="mb-12" id="press-releases">
          <div className="flex items-center gap-2 mb-5">
            <Newspaper className="w-4 h-4 text-emerald-700" />
            <span className="text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-700">News &amp; Press Releases</span>
            <span className="ml-2 text-xs text-muted-foreground">via Pressat</span>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {PRESS_RELEASES.map((pr, i) => (
              <motion.a
                {...fadeIn(0.05 * (i + 1))}
                key={pr.href}
                href={pr.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative block bg-white rounded-2xl border border-slate-200 p-6 hover:border-emerald-400 hover:shadow-lg transition-all"
                data-testid={`press-release-${i}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-700">{pr.tag || pr.publisher}</span>
                  <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-emerald-700 transition-colors" />
                </div>
                <h3 className="font-heading text-base md:text-lg font-bold text-slate-900 leading-snug mb-2 group-hover:text-emerald-800 transition-colors">
                  {pr.title}
                </h3>
                <p
                  className="text-[13px] text-muted-foreground leading-relaxed line-clamp-3"
                  dangerouslySetInnerHTML={{ __html: pr.summary }}
                />
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-[11px] text-slate-400 font-medium">
                    {pr.publisher}{pr.date ? ` · ${pr.date}` : ''}
                  </span>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 group-hover:gap-2 transition-all">
                    Read <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </motion.a>
            ))}
          </div>
        </div>

        {/* Bottom row: Blog + YouTube tiles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Blog tile */}
          <motion.a
            {...fadeIn(0.2)}
            href="https://blog.iiypv.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative overflow-hidden bg-gradient-to-br from-emerald-700 to-emerald-900 text-white rounded-2xl p-7 md:p-8 hover:-translate-y-1 transition-all shadow-lg hover:shadow-2xl"
            data-testid="blog-tile"
          >
            <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-emerald-400/20 blur-2xl" />
            <BookOpen className="w-7 h-7 mb-3 text-emerald-200" />
            <span className="text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-200">The IIY Blog</span>
            <h3 className="font-heading text-2xl md:text-3xl font-bold mt-2 mb-2 leading-tight">
              Natural Health Solutions for an Active Life
            </h3>
            <p className="text-emerald-50/90 text-sm leading-relaxed mb-4">
              Stories, science explained simply, and practical joint-health and active-living routines &mdash; written by the team behind BCM-95®.
            </p>
            <span className="inline-flex items-center gap-1 text-sm font-semibold group-hover:gap-2 transition-all">
              Read the journal <ArrowRight className="w-4 h-4" />
            </span>
          </motion.a>

          {/* YouTube tile */}
          <motion.a
            {...fadeIn(0.3)}
            href="https://www.youtube.com/@iiyphytoventra"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl p-7 md:p-8 hover:-translate-y-1 transition-all shadow-lg hover:shadow-2xl"
            data-testid="youtube-tile"
          >
            <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-red-500/25 blur-2xl" />
            <div className="w-12 h-12 rounded-xl bg-[#FF0000] flex items-center justify-center mb-3 shadow-md">
              <Play className="w-6 h-6 text-white fill-white" />
            </div>
            <span className="text-[10px] uppercase tracking-[0.24em] font-bold text-red-300 inline-flex items-center gap-1.5">
              <Youtube className="w-3.5 h-3.5" /> IIY on YouTube
            </span>
            <h3 className="font-heading text-2xl md:text-3xl font-bold mt-2 mb-2 leading-tight">
              Watch the Science of Nature
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed mb-4">
              Short, jargon-free videos on curcumin, bioavailability and active living &mdash; straight from the IIY Phytoventra team. Subscribe to follow along.
            </p>
            <span className="inline-flex items-center gap-1 text-sm font-semibold group-hover:gap-2 transition-all">
              Subscribe on YouTube <ArrowRight className="w-4 h-4" />
            </span>
          </motion.a>
        </div>
      </div>
    </section>
  );
}
